import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  type FC,
  type ReactNode,
} from 'react';
import { useDispatch } from 'react-redux';

import { isB2B } from '@/domains/core/settings/utils';
import type { Detail } from '@/domains/productDiscovery/ProductPage/b2c/interfaces/Detail.model';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';
import type { AddToCartPayload } from '@/domains/shoppingCart/AddToCart/AddToCart.actions';
import { useReviewsStats } from '@/productDiscovery/ArticleReviews/ReviewsStats.ducks';
import { addToCartWatch } from '@/productDiscovery/Recommendation/modules/ProductBundle/sagas/WatchAddToCart/watchAddToCart.actions';

import { useBundleTracking } from '../../hooks/useBundleTracking/useBundleTracking';
import { PLAYLIST_LOCATION } from '../../hooks/useBundleTracking/useBundleTracking.interfaces';
import { useProductBundleRecommendations } from '../../hooks/useRecommendations/useRecommendations';
import { detailToProduct, productToTrackedDetails } from '../../utils/mappers';
import { ProductBundleContext } from './ProductBundle.context';
import {
  EProductBundleActionTypes,
  type IProduct,
} from './productBundle.interfaces';
import { productBundleReducer } from './productBundle.reducer';
import { initialState } from './productBundle.state';

export const ProductBundleProvider: FC<{ children: ReactNode }> = ({
  children,
}): JSX.Element => {
  const [productBundle, productBundleDispatch] = useReducer(
    productBundleReducer,
    initialState,
  );

  const [state, recommendation, hasProducts, skeletons] =
    useProductBundleRecommendations();

  const {
    ref,
    handleProductCardClick,
    playlistFingerprint,
    playlistReconciliation,
  } = useBundleTracking(
    productBundle.productList,
    recommendation?.[0]?.originalName,
  );

  /* Get Main product */
  const productDetail = useDetail();
  const brand = useBrand();
  const ratingStats = useReviewsStats();
  const productList: Array<Product> = useMemo(() => {
    const mainProduct: Product = detailToProduct(
      productDetail as Detail,
      brand,
      {
        average: ratingStats.average,
        total: ratingStats.total,
      },
    );
    /* Get complementary products */
    const complementaryProducts = recommendation[0]?.products?.slice(0, 2);

    return [mainProduct, ...(complementaryProducts || [])];
  }, [brand, productDetail, recommendation, ratingStats]);

  useEffect(() => {
    const hasAnyProductVat =
      isB2B() &&
      productList.some((product) => product.bundlePrices.taxKey === 'with_vat');

    const mappedList: Array<IProduct> = hasAnyProductVat
      ? productList.map((product) => ({
          ...product,
          bundlePrices: {
            ...product.bundlePrices,
            main: product.bundlePrices.with_vat,
            taxKey: 'with_vat',
          },
          isInBundle: true,
        }))
      : productList.map((product) => ({ ...product, isInBundle: true }));

    productBundleDispatch({
      type: EProductBundleActionTypes.FILL_LIST,
      payload: mappedList.length > 1 ? mappedList : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList && productList.length]);

  useEffect(() => {
    if (!state.isFetching) {
      productBundleDispatch({
        type: EProductBundleActionTypes.SET_IS_LOADING,
        payload: false,
      });
    }
  }, [state.isFetching]);

  const dispatch = useDispatch();
  const handleAddToCart = useCallback(async () => {
    const payload: AddToCartPayload = {
      showConfirmationMessage: false,
      products: productBundle.productList
        .filter((product) => product.isInBundle)
        .map((product, index) =>
          productToTrackedDetails(
            product,
            index,
            PLAYLIST_LOCATION.B2C_PRODUCT_PAGE,
            recommendation?.[0]?.originalName,
          ),
        ),
      playlistReconciliation: {
        playlistFingerprint,
        playlistReconciliation,
      },
    };

    dispatch(addToCartWatch(payload));
  }, [
    productBundle.productList,
    playlistFingerprint,
    playlistReconciliation,
    dispatch,
    recommendation,
  ]);

  const providerValue = useMemo(
    () => ({
      ref,
      state: {
        isFetching: productBundle.state.isFetching,
        error: state.error,
      },
      productList: productBundle.productList,
      productBundleDispatch,
      hasProducts,
      skeletons,
      handleAddToCart,
      handleProductCardClick,
    }),
    [
      handleAddToCart,
      handleProductCardClick,
      hasProducts,
      productBundle.productList,
      productBundle.state.isFetching,
      ref,
      skeletons,
      state.error,
    ],
  );

  return (
    <ProductBundleContext.Provider value={providerValue}>
      {children}
    </ProductBundleContext.Provider>
  );
};

ProductBundleProvider.displayName = 'ProductBundleProvider';
